import { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert, FileWarning, CheckCircle, Camera, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PuliziePage = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    serviceType: '',
    squareMeters: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact/pulizie`, formData);
      setIsSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-text-primary/95 to-text-primary/70" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-primary font-medium text-sm tracking-widest uppercase mb-4 block">
              Servizio Pulizie Professionali
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Smetti di rischiare <span className="text-primary">43k euro di multa</span> per risparmiarne 500 l'anno a Trieste
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Affida la pulizia della tua struttura a Kilolava
            </p>
            <Button 
              data-testid="pulizie-hero-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full shadow-[0_20px_50px_rgb(233,30,99,0.3)]"
              onClick={() => document.getElementById('contact-pulizie')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <ShieldAlert className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-6">
              Sapevi che rivolgendoti a personale non qualificato
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              In caso di infortunio o ispezione rischi fino a <strong className="text-primary">43k euro di multa</strong>?
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-red-50 border border-red-200 rounded-2xl p-8"
            >
              <FileWarning className="w-12 h-12 text-red-600 mb-4" />
              <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
                Responsabilità Penali
              </h3>
              <p className="text-text-secondary leading-relaxed">
                + eventuali responsabilità penali per l'amministratore della struttura 
                (immagina essere processato per tentato omicidio dopo che il tuo addetto è inciampato sulle scale)
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gray-100 border border-gray-200 rounded-2xl p-8"
            >
              <p className="text-text-secondary leading-relaxed italic">
                "Ai sensi del D.Lgs 81/2008, l'amministratore agisce come committente. 
                Se affida i lavori a una ditta non in regola col DURC o senza le necessarie 
                coperture assicurative, risponde personalmente delle sanzioni e dei danni, 
                oltre a esporre il condominio a rivalse economiche."
              </p>
            </motion.div>
          </div>

          <div className="text-center">
            <Button 
              data-testid="pulizie-mid-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-pulizie')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni
            </Button>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Con Kilolava, questo problema non può verificarsi
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: CheckCircle,
                title: "In Regola",
                description: "Operiamo nel rispetto di tutte le leggi"
              },
              {
                icon: Camera,
                title: "Foto e Video",
                description: "I nostri addetti inviano foto e video prima e dopo la pulizia"
              },
              {
                icon: Clock,
                title: "Procedure Personalizzate",
                description: "Creiamo procedure su misura per ogni struttura con il miglior rapporto qualità/prezzo"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/80">{item.description}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              data-testid="pulizie-solution-cta"
              size="lg"
              className="bg-white text-primary hover:bg-white/90 px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-pulizie')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni
            </Button>
          </div>
        </div>
      </section>

      {/* Urgency Section */}
      <section className="py-20 bg-text-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ma Attenzione
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Essendo un servizio che richiede un'organizzazione complessa non possiamo svolgerlo per tutte le strutture di Trieste.
              <br /><br />
              <strong className="text-primary">Per questo ti consigliamo vivamente di affrettarti a richiedere informazioni</strong>
            </p>
            <Button 
              data-testid="pulizie-urgency-cta"
              size="lg"
              className="bg-primary hover:bg-primary-hover text-white px-8 py-6 text-lg rounded-full"
              onClick={() => document.getElementById('contact-pulizie')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Richiedi Informazioni Ora
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-pulizie" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary mb-4">
              Richiedi Informazioni per <span className="text-primary">Pulizie</span>
            </h2>
            <p className="text-text-secondary">Compila il modulo e ti ricontatteremo entro 24 ore</p>
          </motion.div>

          {isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-primary/5 border border-primary/20 rounded-3xl p-12 text-center"
            >
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
              <h3 className="font-heading text-2xl font-bold text-text-primary mb-4">
                Richiesta Inviata!
              </h3>
              <p className="text-text-secondary">Ti ricontatteremo presto.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome Azienda/Struttura *</Label>
                  <Input
                    data-testid="pulizie-form-company"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    placeholder="Es. Hotel Miramare"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Referente *</Label>
                  <Input
                    data-testid="pulizie-form-contact"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    placeholder="Mario Rossi"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    data-testid="pulizie-form-email"
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="email@esempio.com"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input
                    data-testid="pulizie-form-phone"
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+39 333 1234567"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="serviceType">Tipo Struttura *</Label>
                  <Select onValueChange={(value) => setFormData({...formData, serviceType: value})}>
                    <SelectTrigger data-testid="pulizie-form-type" className="h-12">
                      <SelectValue placeholder="Seleziona tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="casa-vacanza">Casa Vacanza</SelectItem>
                      <SelectItem value="affittacamere">Affittacamere</SelectItem>
                      <SelectItem value="hotel">Hotel</SelectItem>
                      <SelectItem value="ufficio">Ufficio</SelectItem>
                      <SelectItem value="condominio">Condominio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="squareMeters">Metri Quadri (circa)</Label>
                  <Input
                    data-testid="pulizie-form-sqm"
                    id="squareMeters"
                    name="squareMeters"
                    value={formData.squareMeters}
                    onChange={handleChange}
                    placeholder="Es. 150"
                    className="h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Messaggio</Label>
                <Textarea
                  data-testid="pulizie-form-message"
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Raccontaci le tue esigenze..."
                  rows={4}
                />
              </div>

              <Button
                data-testid="pulizie-form-submit"
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary-hover text-white py-6 text-lg rounded-full"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default PuliziePage;
